import { Code, ConnectError } from "@connectrpc/connect";
import { useMutation, useMutationState, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo } from "react";
import { z } from "zod";

import { useToast } from "@/components/flexkit/toast";
import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { useOnaEnabledForOrg } from "@/hooks/use-ona-enabled";
import { usePreferenceValue } from "@/hooks/use-preference-value";
import { useGetAccount } from "@/queries/account-queries";
import { organizationQueryKey, type PlainGetBillingInfoResponse } from "@/queries/organization-queries";
import { useSetPreference } from "@/queries/user-preferences-queries";
import { useAuthenticatedUser, type PlainUser } from "@/queries/user-queries";
import { formatError } from "@/utils/errors";
import { PaymentMethodStatus } from "gitpod-next-api/gitpod/v1/billing_pb";
import { OrganizationRole } from "gitpod-next-api/gitpod/v1/organization_pb";

export const ORGS_PENDING_ONBOARDING_PREFERENCE_KEY = "ORGS_PENDING_ONBOARDING";
export const IS_ONA_ONBOARDED_PREFERENCE_KEY = "IS_ONA_ONBOARDED";
export const WELCOME_MESSAGES_SHOWN_PREFERENCE_KEY = "WELCOME_MESSAGES_SHOWN";

const PendingOnboardingOrgIds = z.array(z.string().nonempty());

export const useOnboardingCompletion = () => {
    const { data: account, isLoading: isLoadingAccount, error: loadAccountError } = useGetAccount();
    const { data: user, isLoading: isLoadingUser, error: fetchUserError } = useAuthenticatedUser();
    const {
        value: isOnaOnboardedValue,
        setValueAsync: setIsOnaOnboardedValue,
        queryData: { isLoading: isLoadingPreference, refetch: refetchIsOnaOnboardedValue, error: fetchPreferenceError },
    } = usePreferenceValue(IS_ONA_ONBOARDED_PREFERENCE_KEY);
    const { value: isOnaEnabledForOrg, loading: isLoadingOnaEnabledForOrg } = useOnaEnabledForOrg();
    const { mutateAsync: setPreferenceAsync } = useSetPreference();
    const queryClient = useQueryClient();
    const api = useGitpodAPI();
    const { toast } = useToast();

    const membership = account?.memberships?.find((membership) => membership.userId === user?.id);

    const {
        mutate: migrateOnboardingCompletionStatus,
        data: hasMigratedOnboardingCompletionStatus,
        isPending: isMigratingOnboardingCompletionStatus,
        error: migrateOnboardingCompletionStatusError,
    } = useMutation({
        mutationKey: ["migrationOnboardingCompletionStatus"],
        mutationFn: async ({ user }: { user: PlainUser }) => {
            const fetchPreferenceByKey = async (key: string): Promise<string> => {
                try {
                    const { preference } = await api.userService.getPreference({
                        preference: { case: "preferenceKey", value: key },
                    });
                    return preference?.value ?? "";
                } catch (e) {
                    if (e instanceof ConnectError && e.code === Code.NotFound) {
                        return "";
                    }
                    throw e;
                }
            };

            const [legacyOrgsPendingOnboardingValue, isOnaOnboardedValue] = await Promise.all([
                fetchPreferenceByKey(ORGS_PENDING_ONBOARDING_PREFERENCE_KEY),
                fetchPreferenceByKey(IS_ONA_ONBOARDED_PREFERENCE_KEY),
            ]);

            // a flag that determines whether we need to migrate from the legacy onboarding to the new onboarding preference value
            let needsMigration = false;
            let needsOnboarding = false;

            if (legacyOrgsPendingOnboardingValue === "" && isOnaOnboardedValue === "") {
                needsMigration = true;
                // user preference does not contain signal for whether onboarding is needed
                // query billing info to determine if onboarding is needed
                try {
                    const billingInfo: PlainGetBillingInfoResponse = await queryClient.ensureQueryData({
                        queryKey: organizationQueryKey.getBillingInfo(user.organizationId),
                        queryFn: () => api.billingService.getBillingInfo({ organizationId: user.organizationId }),
                    });
                    needsOnboarding = billingInfo.paymentMethodStatus !== PaymentMethodStatus.VERIFIED;
                } catch (error) {
                    if (error instanceof ConnectError && error.code === Code.NotFound) {
                        // no billing info found, needs onboarding
                        needsOnboarding = true;
                    } else {
                        throw error;
                    }
                }
            } else if (isOnaOnboardedValue !== "") {
                needsMigration = false;
                needsOnboarding = isOnaOnboardedValue === "false";
            } else {
                needsMigration = true;

                const { success, data: legacyOrgsPendingOnboarding } = PendingOnboardingOrgIds.safeParse(
                    JSON.parse(legacyOrgsPendingOnboardingValue),
                );
                if (success) {
                    needsOnboarding = legacyOrgsPendingOnboarding.includes(user.organizationId);
                } else {
                    // corrupted value, probably not gonna happen but let's assume onboarding is needed
                    needsOnboarding = true;
                }
            }

            if (needsMigration) {
                await setPreferenceAsync({
                    key: IS_ONA_ONBOARDED_PREFERENCE_KEY,
                    value: needsOnboarding ? "false" : "true",
                });
                if (needsOnboarding) {
                    await setPreferenceAsync({
                        key: WELCOME_MESSAGES_SHOWN_PREFERENCE_KEY,
                        value: "false",
                    });
                }
            }

            return true;
        },
        onError: (error) => {
            console.error("Error while migrating onboarding completion status", error);
            toast({
                title: "Error while loading onboarding completion status",
                description: formatError(error),
            });
        },
    });
    const mutationState = useMutationState({
        filters: { mutationKey: ["migrationOnboardingCompletionStatus"] },
    });
    const hasMigrationStarted = useMemo(() => {
        return mutationState.length > 0;
    }, [mutationState]);

    const markOnboardingAsPending = useCallback(async () => {
        await setIsOnaOnboardedValue("false");
        await setPreferenceAsync({ key: WELCOME_MESSAGES_SHOWN_PREFERENCE_KEY, value: "false" });
        await refetchIsOnaOnboardedValue();
    }, [refetchIsOnaOnboardedValue, setIsOnaOnboardedValue, setPreferenceAsync]);

    const markOnboardingAsComplete = useCallback(async () => {
        await setIsOnaOnboardedValue("true");
        await refetchIsOnaOnboardedValue();
    }, [refetchIsOnaOnboardedValue, setIsOnaOnboardedValue]);

    // migrate onboarding preference value from the legacy onboarding to the new onboarding preference value
    // this is a one-time migration, so we only need to do it once
    useEffect(() => {
        if (
            user &&
            !hasMigratedOnboardingCompletionStatus &&
            !isMigratingOnboardingCompletionStatus &&
            !isLoadingOnaEnabledForOrg &&
            isOnaEnabledForOrg &&
            !hasMigrationStarted
        ) {
            migrateOnboardingCompletionStatus({ user });
        }
    }, [
        user,
        hasMigratedOnboardingCompletionStatus,
        isMigratingOnboardingCompletionStatus,
        migrateOnboardingCompletionStatus,
        hasMigrationStarted,
        isLoadingOnaEnabledForOrg,
        isOnaEnabledForOrg,
    ]);

    return {
        markOnboardingAsPending,
        markOnboardingAsComplete,
        isPendingOnboarding:
            membership?.userRole === OrganizationRole.ADMIN && isOnaEnabledForOrg && isOnaOnboardedValue !== "true",
        isLoadingOnboardingCompletion:
            isMigratingOnboardingCompletionStatus ||
            isLoadingPreference ||
            isLoadingAccount ||
            isLoadingUser ||
            isLoadingOnaEnabledForOrg,
        error: fetchUserError || fetchPreferenceError || migrateOnboardingCompletionStatusError || loadAccountError,
    };
};
